<template>
  <v-menu
    ref="menu"
    v-model="open"
    :content-class="classes"
    :transition="false"
    :location="props.location"
    :offset="props.offset"
    :target="props.target"
    :location-strategy="props.locationStrategy"
    :viewport-margin="props.viewportMargin"
    :close-on-content-click="props.closeOnContentClick"
    :open-on-hover="props.openOnHover"
    :eager="props.eager"
    :attach="props.attach"
    @action="$emit('action', $event)"
    @keyup="$emit('keyup', $event)"
    @focus="$emit('focus', $event)"
    @blur="$emit('blur', $event)"
  >
    <template #activator="slotProps">
      <slot name="activator" v-bind="slotProps" />
    </template>

    <slot v-if="slots.menu" name="menu" />
    <ui-menu-content
      v-else
      ref="menuContent"
      :submenu="props.submenu"
      :filter="props.filter"
      :items="props.items"
      :menu-props="props.menuProps"
      :selected="props.selected"
      @action="handleAction"
    >
      <template v-if="slots.item" #item="{ item }">
        <slot name="item" :item="item" />
      </template>
    </ui-menu-content>
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue';
import UiMenuContent from './content.vue';

const props = withDefaults(
  defineProps<{
    modelValue?: boolean;
    contentClass?: string;
    menuKeyOfSelectedItem?: string;
    value?: boolean;
    hasTriangle?: boolean;
    items?: Bemob.ListItem[];
    submenu?: boolean;
    filter?: boolean;
    menuProps?: any;
    openOnHover?: boolean;
    stickToRight?: boolean;
    selected?: string;
    closeOnContentClick?: boolean;
    eager?: boolean;
    attach?: boolean | string | Element;
    location?: any;
    offset?: [number, number];
    target?: Element | string | [number, number] | null;
    locationStrategy?: string | ((...args: unknown[]) => { updateLocation?: () => void } | void);
    viewportMargin?: number;
  }>(),
  {
    items: () => [] as Bemob.ListItem[],
    closeOnContentClick: false,
    location: 'bottom start',
    offset: () => [0, 8],
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'input', value: boolean): void;
  (e: 'action', value?: unknown): void;
  (e: 'keyup', value: KeyboardEvent): void;
  (e: 'focus', value: FocusEvent): void;
  (e: 'blur', value: FocusEvent): void;
}>();

const slots = useSlots();
const menu = ref<any>(null);
const menuContent = ref<{ clear: () => void } | null>(null);

const open = computed({
  get() {
    if (typeof props.modelValue === 'boolean') {
      return props.modelValue;
    }
    return !!props.value;
  },
  set(value: boolean) {
    emit('update:modelValue', value);
    emit('input', value);
  },
});

watch(
  () => open.value,
  value => {
    if (value) {
      menuContent.value?.clear();
    }
  },
);

const classes = computed(() => {
  const result = ['ui-menu'];
  if (props.contentClass) result.push(props.contentClass);
  if (slots.menu && !props.hasTriangle) result.push('ui-menu--custom-content');
  if (props.hasTriangle) result.push('ui-menu--has-triangle');
  if (props.stickToRight) result.push('ui-menu--stick-to-right');
  if (props.submenu) result.push('ui-menu--submenu');
  return result.join(' ');
});

const handleAction = () => {
  emit('action');
  open.value = false;
};

const toggle = (status: boolean = !open.value) => {
  open.value = status;
};

const redraw = () => {
  menu.value?.updateLocation?.();
};

defineExpose({ toggle, redraw });
</script>

<style lang="scss">
.ui-menu {
  border-radius: 8px;

  &--submenu {
    border-radius: 0 0 8px 8px;
  }

  &--stick-to-right {
    margin-left: 11px;
  }

  &__triangle {
    position: absolute;
    content: '';
    top: -4px;
    width: 8px;
    height: 8px;
    background-color: var(--color-white);
    transform: rotate(45deg);
    box-shadow: -5px -5px 10px -2px var(--color-black20);
  }

  &--has-triangle {
    margin-top: 5px;
    contain: initial;
    overflow: visible;
    box-shadow: 0 4px 16px 0 var(--color-black20) !important;
  }
}

.v-overlay__content.ui-menu {
  border-radius: 8px;
}

.v-overlay__content.ui-menu.ui-menu--submenu {
  border-radius: 0 0 8px 8px;
}

.v-overlay__content.ui-menu.ui-menu--stick-to-right {
  margin-left: 11px;
}

.v-overlay__content.ui-menu.ui-menu--custom-content:not(.ui-menu--has-triangle) {
  box-shadow: 0 4px 16px 0 var(--color-black20) !important;
}

.v-overlay__content.ui-menu.ui-menu--custom-content:not(.ui-menu--has-triangle):not(
    .bookmark-list__menu
  ):not(.main-menu-events):not(.main-menu-workspace):not(.navbar-tablet-menu)
  > :first-child {
  border-radius: inherit;
  overflow: hidden;
}
</style>
